import classNames from 'classnames';
import { Identifier, TargetType } from 'dnd-core';
import React from 'react';
import type { ConnectDropTarget, DropTargetConnector, DropTargetMonitor, DropTargetSpec, DndComponentClass } from 'react-dnd';
import { DropTarget } from 'react-dnd';

import TableRowHoverPosition from 'sharedreact/data/TableRowHoverPosition';

type DropTargetComponentProps = {
  dropItemType: Identifier | null,
  isOver: boolean,
  monitor: DropTargetMonitor,

  connectDropTarget: ConnectDropTarget
};

type Props<T> = DropTargetComponentProps & {
  item: T,
  className?: string,
  children: React.ReactNode,
  innerRef: React.ForwardedRef<HTMLTableRowElement>;

  onClick?: React.MouseEventHandler<HTMLTableRowElement>,
  onHover: (sourceItem: any, targetItem: T, position: string) => void,
  onDrop: (sourceItem: any, targetItem: T, position: string) => void,
  getClassNameOnDragHover?: (sourceItem: any, targetItem: T, position: string) => string,
  itemAcceptsDropItemTypes?: (item: T) => string[]
};

class TableRowMoveTargetComponent<T> extends React.Component<Props<T>> {
  private elementRef: HTMLTableRowElement | null;

  constructor (props: Props<T>) {
    super(props);
    this.elementRef = null;

    this._getHoveringPosition = this._getHoveringPosition.bind(this);
  }

  _getHoveringPosition (): string | undefined {
    const node = this.elementRef;

    if (node instanceof HTMLTableRowElement) {
      // Determine rectangle on screen
      const hoverBoundingRect = node.getBoundingClientRect();

      // Get vertical middle
      const hoverQuarterY: number = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 4;
      const hoverHalfY: number = hoverQuarterY * 2;
      const hoverThreeQuarterY: number = hoverQuarterY * 3;

      // Determine mouse position
      const clientOffset = this.props.monitor.getClientOffset() ?? {x: 0, y: 0};
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      let position: string;
      if (hoverClientY < hoverQuarterY) {
        position = TableRowHoverPosition.TOP;
      } else if (hoverClientY < hoverHalfY) {
        position = TableRowHoverPosition.CENTER_TOP;
      } else if (hoverClientY < hoverThreeQuarterY) {
        position = TableRowHoverPosition.CENTER_BOTTOM;
      } else {
        position = TableRowHoverPosition.BOTTOM;
      }

      return position;
    }
    return undefined;
  }

  override render (): React.ReactNode {
    const { className,
      item,
      onHover, onDrop, itemAcceptsDropItemTypes, // eslint-disable-line no-unused-vars
      children,
      onClick,
      getClassNameOnDragHover,
      isOver,
      monitor,
      connectDropTarget
    } = this.props;

    let hoverClassName: string = '';
    if (getClassNameOnDragHover != null && isOver && monitor.getItem()) {
      const sourceItem = monitor.getItem().item;

      if (sourceItem !== item) {
        const position: string | undefined = this._getHoveringPosition();
        if (position != null) {
          hoverClassName = getClassNameOnDragHover(sourceItem, item, position);
        }
      }
    }

    return connectDropTarget(
      <tr ref={this.props.innerRef} className={classNames(className, hoverClassName)} onClick={onClick}>
        {children}
      </tr>
    );
  }
}

function acceptsTarget<T> (props: Omit<Props<T>, keyof DropTargetComponentProps>): TargetType {
  return props.itemAcceptsDropItemTypes && props.item != null ? props.itemAcceptsDropItemTypes(props.item) : [];
}

function collect (connect: DropTargetConnector, monitor: DropTargetMonitor): DropTargetComponentProps {
  return {
    connectDropTarget: connect.dropTarget(),
    dropItemType: monitor.getItemType(),
    isOver: monitor.isOver(),
    monitor: monitor
  };
}

class _TableRowMoveTarget<T> extends React.PureComponent<Omit<Props<T>, keyof DropTargetComponentProps>> {
  private _tableRowMoveTargetDropTarget:  React.ComponentClass<Omit<Props<T>, keyof DropTargetComponentProps>>;
  
  constructor (props: Omit<Props<T>, keyof DropTargetComponentProps>) {
    super(props);

    
    const moveTarget: DropTargetSpec<Omit<Props<T>, keyof DropTargetComponentProps>> = {
      drop<T> (props: Omit<Props<T>, keyof DropTargetComponentProps>, monitor: DropTargetMonitor, component: TableRowMoveTargetComponent<T>) {
        const position: string | undefined = component._getHoveringPosition();
        if (position != null) {
          const sourceItem = monitor.getItem().item;
          const targetItem = props.item;
          if (sourceItem !== targetItem && targetItem != null) {
            props.onDrop(sourceItem, targetItem, position);
          }
        }
      },
      hover<T> (props: Omit<Props<T>, keyof DropTargetComponentProps>, monitor: DropTargetMonitor, component: TableRowMoveTargetComponent<T>) {
        // Force update the component to retrigger an update so that upper/center/bottom decorations are shown correctly.
        // Without this, only the initial position when entering the item will be remembered.
        component.forceUpdate();

        const position: string | undefined = component._getHoveringPosition();
        if (position != null) {
          const sourceItem = monitor.getItem().item;
          const targetItem = props.item;
          if (sourceItem !== targetItem && targetItem !=  null) {
            props.onHover(sourceItem, targetItem, position);
          }
        }
      }
    };

    this._tableRowMoveTargetDropTarget = DropTarget<Omit<Props<T>, keyof DropTargetComponentProps>, DropTargetComponentProps>(
      acceptsTarget,
      moveTarget,
      collect
    )(TableRowMoveTargetComponent<T>);
  }

  override render (): React.ReactNode {
    const TableRowMoveTargetDropTarget = this._tableRowMoveTargetDropTarget;

    return (
      <TableRowMoveTargetDropTarget {...this.props} />
    );
  }
}

type TableRowMoveTargetProps<T> = Omit<ConstructorParameters<typeof _TableRowMoveTarget<T>>[0], 'innerRef'>; 

declare module "react" {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: React.Ref<T>) => JSX.Element
  ): (props: P & React.RefAttributes<T>) => JSX.Element;
}

function TableRowMoveTargetRenderer<T> (
  props: TableRowMoveTargetProps<T>,
  ref: React.ForwardedRef<HTMLTableRowElement>
) {
  return <_TableRowMoveTarget innerRef={ref} {...props} />;
}

const TableRowMoveTarget = React.forwardRef(TableRowMoveTargetRenderer);

export default TableRowMoveTarget;

